import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="menu-dropdown"
export default class extends Controller {
  static targets = ["menuBox", "menuBoxBackground", "menuBoxContent", "menuButton", "navBarMain", "navLinks", "megaTrigger", "panel"]

  connect() {
    this.closeTimer = null
    this.navHovered = false
    this.isMenuOpen = false
    this.transitionDurationMs = 500
    this.activePanelId = null
    this.boundPointerOver = this.handlePointerOver.bind(this)
    this.element.addEventListener("pointerover", this.boundPointerOver, true)

    if (!this.hasMenuBoxTarget) {
      return
    }
    this.menuBoxTarget.style.display = ""
    this.menuBoxTarget.classList.remove("hidden")
    this.menuBoxTarget.classList.add(
      "overflow-hidden",
      "transition-[height,padding-bottom]",
      "duration-500",
      "ease-in-out"
    )
    this.menuBoxTarget.style.height = "0px"
    this.menuBoxTarget.style.paddingBottom = "0px"

    this.hidePanels()

  }

  disconnect() {
    this.clearCloseTimer()
    this.element.removeEventListener("pointerover", this.boundPointerOver, true)
  }

  navHover() {
    this.navHovered = true
    this.clearCloseTimer()
  }

  navHoverEnd(){
    this.navHovered = false
    this.scheduleCloseStylesReset()
  }

  openMenuDropdown(){
    this.clearCloseTimer()
    this.isMenuOpen = true
    this.toggleMenuDropdown(true)
    this.applyActiveNavStyles()
  }

  openPanel(event) {
    const panelId = event.params.panel || event.currentTarget?.dataset?.menuDropdownPanelParam
    this.openPanelById(panelId, event.currentTarget)
  }

  handlePointerOver(event) {
    const trigger = event.target.closest("[data-menu-dropdown-panel-param]")
    if (!trigger || !this.element.contains(trigger)) return

    const panelId = trigger.dataset.menuDropdownPanelParam
    this.openPanelById(panelId, trigger)
  }

  openPanelById(panelId, trigger) {
    const normalizedPanelId = panelId?.replace(/^["']+|["']+$/g, "")
    if (!normalizedPanelId) {
      return
    }

    if (!this.panelTargets.some((panel) => panel.dataset.panelId === normalizedPanelId)) {
      return
    }

    this.activePanelId = normalizedPanelId
    this.showPanel(normalizedPanelId)
    this.updateActiveTrigger(normalizedPanelId)
    this.openMenuDropdown()
  }

  closeMenuDropdown(){
    this.isMenuOpen = false
    this.activePanelId = null
    this.hidePanels()
    this.clearActiveTriggers()
    this.toggleMenuDropdown(false)
    this.scheduleCloseStylesReset()
  }

  toggleMenuDropdown(openMenu){
    if (!this.hasMenuBoxTarget) return

    if (openMenu) {
      this.expandMenuBox()
      return
    }

    this.collapseMenuBox()
  }

  applyActiveNavStyles() {
    this.element.classList.add("menu-dropdown-active")

    if (this.hasNavBarMainTarget) {
      this.navBarMainTarget.classList.add("xl:!bg-white")
    }

    if (this.hasMenuBoxBackgroundTarget) {
      this.menuBoxBackgroundTarget.classList.add("xl:!bg-white")
    }

    this.navLinksTargets.forEach((link) => {
      link.classList.remove("xl:text-white")
      link.classList.add("text-dark-gray-custom")
    })
  }

  applyDefaultNavStyles() {
    this.element.classList.remove("menu-dropdown-active")

    if (this.hasNavBarMainTarget) {
      this.navBarMainTarget.classList.remove("xl:!bg-white")
    }

    if (this.hasMenuBoxBackgroundTarget) {
      this.menuBoxBackgroundTarget.classList.remove("xl:!bg-white")
    }

    this.navLinksTargets.forEach((link) => {
      link.classList.remove("text-dark-gray-custom")
    })
  }

  scheduleCloseStylesReset() {
    this.clearCloseTimer()

    this.closeTimer = setTimeout(() => {
      if (this.isMenuOpen) return
      this.applyDefaultNavStyles()
    }, this.transitionDurationMs)
  }

  clearCloseTimer() {
    if (!this.closeTimer) return
    clearTimeout(this.closeTimer)
    this.closeTimer = null
  }

  showPanel(panelId) {
    this.panelTargets.forEach((panel) => {
      const isActive = panel.dataset.panelId === panelId
      panel.classList.toggle("hidden", !isActive)
    })
  }

  hidePanels() {
    if (!this.hasPanelTarget) return

    this.panelTargets.forEach((panel) => {
      panel.classList.add("hidden")
    })
  }

  expandMenuBox() {
    const height = this.measureOpenHeight()
    this.menuBoxTarget.style.height = `${height}px`
    this.menuBoxTarget.style.paddingBottom = height > 0 ? "24px" : "0px"
  }

  collapseMenuBox() {
    this.menuBoxTarget.style.height = "0px"
    this.menuBoxTarget.style.paddingBottom = "0px"
  }

  measureOpenHeight() {
    if (!this.hasMenuBoxContentTarget) return 0
    return this.menuBoxContentTarget.scrollHeight
  }

  updateActiveTrigger(panelId) {
    if (!this.hasMegaTriggerTarget) return

    this.megaTriggerTargets.forEach((trigger) => {
      const isActive = trigger.dataset.menuDropdownPanelParam === panelId
      trigger.classList.toggle("menu-mega-trigger-active", isActive)
      trigger.querySelector("[data-menu-dropdown-trigger-bar]")?.classList.toggle("scale-x-100", isActive)
      trigger.querySelector("[data-menu-dropdown-trigger-bar]")?.classList.toggle("scale-x-0", !isActive)
    })
  }

  clearActiveTriggers() {
    if (!this.hasMegaTriggerTarget) return

    this.megaTriggerTargets.forEach((trigger) => {
      trigger.classList.remove("menu-mega-trigger-active")
      trigger.querySelector("[data-menu-dropdown-trigger-bar]")?.classList.remove("scale-x-100")
      trigger.querySelector("[data-menu-dropdown-trigger-bar]")?.classList.add("scale-x-0")
    })
  }
}
